/* eslint class-methods-use-this: 0, no-param-reassign: 0 */
import { ApplePackageManagers } from 'client/util/Constants';
import { isEqual } from 'lodash';
import CollectionModule from 'client/store/modules/CollectionModule';
import SoftwareAppsApi from 'client/api/v2/SoftwareAppsApi';

export class SoftwareAppsCollectionClass extends CollectionModule {
  extractModule() {
    const module = super.extractModule();
    module.actions.importVppApps = (...params) => this.importVppApps(...params);
    module.actions.retryInstall = (...params) => this.retryInstall(...params);
    module.actions.fetchTotalFilteredCount = (...params) => this.fetchTotalFilteredCount(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  /**
   * Override method from base class to ignore applied filters when determining
   * whether the SoftwareApps collection can be refreshed.
   */
  canRefreshCollection(state = { features: { items: {} } }, getters = {}) {
    const { features } = state;
    const { boundItemsUrl, showBoundItems } = features;
    const { searchTerm } = features.items;
    const { getItemsToBind = [], getItemsToUnbind = {} } = getters;
    const hasSelectionChanged = getItemsToBind.length > 0 || getItemsToUnbind.length > 0;

    return !((boundItemsUrl && showBoundItems) || searchTerm || hasSelectionChanged);
  }

  /**
   * Override method from base class to disable the checkbox if
   * the packageManager is different to APPLE_VPP
   */
  isItemSelectable() {
    return item => item.settings[0].packageManager !== ApplePackageManagers.appleVpp;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * Override action from base class to call first the importVppApps action before the
   * base class function.
   */
  async fetchCollection(context, { next = false } = {}) {
    const { state } = context;
    const vppFilter = { name: 'packageManager', operator: 'in', value: Object.values(ApplePackageManagers).join('|') };

    if (state.features.items.filters.find(filter => isEqual(filter, vppFilter))) {
      try {
        await context.dispatch('importVppApps');
      } catch {
        return super.fetchCollection(context, { next });
      }
    }

    return super.fetchCollection(context, { next });
  }

  importVppApps() {
    return SoftwareAppsApi.importApps({ packageManager: ApplePackageManagers.appleVpp });
  }

  /**
   * Override method from base class to call first the importVppApps action before the
   * base class function.
   */
  refreshCollection(context, { item, skip = context.state.features.items.pagination.skip } = {}) {
    const { state } = context;
    const vppFilter = { name: 'packageManager', operator: 'in', value: Object.values(ApplePackageManagers).join('|') };

    if (state.features.items.filters.find(filter => isEqual(filter, vppFilter))) {
      return context.dispatch('importVppApps')
        .then(() => {
          super.refreshCollection(context, { item, skip });
        });
    }

    return super.refreshCollection(context, { item, skip });
  }
}

export default new SoftwareAppsCollectionClass(SoftwareAppsApi, 'id', 'displayName').extractModule();
